import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { AlertCircle, Loader2, Plus, Trash2, Star } from 'lucide-react';
import type { Card } from '../../types/card';
import { useAuth } from '../../lib/auth';

interface FormData {
  prenom: string;
  photo: File | null;
}

export default function Admin() {
  const { user } = useAuth();
  const [cards, setCards] = useState<Card[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    prenom: '',
    photo: null
  });

  const fetchCards = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Session non valide');
      }

      const { data, error } = await supabase
        .from('cards')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCards(data || []);
    } catch (err) {
      console.error('Error fetching cards:', err);
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCards();
  }, []);

  const handleCreateSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    const trimmedPrenom = formData.prenom.trim();
    if (!trimmedPrenom) {
      setError('Le prénom est requis');
      return;
    }

    if (!user) {
      setError('Utilisateur non authentifié');
      return;
    }

    setCreateLoading(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Non authentifié');
      }

      const formDataToSend = new FormData();
      formDataToSend.append('prenom', trimmedPrenom);
      formDataToSend.append('owner_id', user.id);
      if (formData.photo) {
        formDataToSend.append('photo', formData.photo);
      }

      const response = await fetch('/api/generate-cards', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        },
        body: formDataToSend,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || 'Erreur lors de la génération des cartes');
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Erreur lors de la génération des cartes');
      }

      setShowCreateForm(false);
      setFormData({ prenom: '', photo: null });
      await fetchCards();
    } catch (err) {
      console.error('Erreur:', err);
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeleteCard = async (cardId: string) => {
    try {
      const { error } = await supabase
        .from('cards')
        .delete()
        .eq('id', cardId);

      if (error) throw error;
      
      await fetchCards();
    } catch (err) {
      console.error('Error deleting card:', err);
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    }
  };

  const calculateTotalScore = (card: Card) => {
    return card.attaque + card.defense + card.special;
  };

  const getRarityStyle = (rarete: string) => {
    switch (rarete) {
      case 'COMMUN':
        return 'border-2 border-gray-300';
      case 'RARE':
        return 'border-4 border-blue-500 shadow-lg shadow-blue-500/50';
      case 'GOLD':
        return 'border-4 border-yellow-500 shadow-lg shadow-yellow-500/50';
      default:
        return 'border border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
          <p className="mt-2 text-gray-600">Chargement des cartes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Administration</h1>
          <p className="mt-2 text-sm text-gray-700">
            Gérez les cartes du jeu ici
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={() => {
              setError(null);
              setShowCreateForm(true);
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            Créer des cartes
          </button>
        </div>
      </div>

      {error && (
        <div className="mt-6 rounded-md bg-red-50 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {showCreateForm && (
        <div className="mt-6 bg-white shadow rounded-lg p-6">
          <form onSubmit={handleCreateSubmit} className="space-y-6">
            <div>
              <label htmlFor="prenom" className="block text-sm font-medium text-gray-700">
                Prénom
              </label>
              <input
                type="text"
                id="prenom"
                value={formData.prenom}
                onChange={(e) => {
                  setError(null);
                  setFormData(prev => ({ ...prev, prenom: e.target.value }));
                }}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                required
                minLength={2}
                maxLength={50}
                pattern="[A-Za-zÀ-ÿ\s-]+"
                title="Le prénom ne doit contenir que des lettres, espaces et tirets"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Photo (optionnelle)
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    const file = e.target.files[0];
                    if (file.size > 5 * 1024 * 1024) {
                      setError('La taille de l\'image ne doit pas dépasser 5 Mo');
                      return;
                    }
                    setError(null);
                    setFormData(prev => ({ ...prev, photo: file }));
                  }
                }}
                className="mt-1 block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-full file:border-0
                  file:text-sm file:font-semibold
                  file:bg-indigo-50 file:text-indigo-700
                  hover:file:bg-indigo-100"
              />
              <p className="mt-1 text-sm text-gray-500">
                Format accepté : JPG, PNG. Taille maximale : 5 Mo
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  setShowCreateForm(false);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={createLoading || !formData.prenom.trim()}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {createLoading ? (
                  <>
                    <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                    Création en cours...
                  </>
                ) : (
                  'Créer'
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map((card) => (
          <div
            key={card.id}
            className={`relative bg-white rounded-lg shadow-md overflow-hidden ${getRarityStyle(card.rarete)}`}
          >
            {card.image_url && (
              <img
                src={card.image_url}
                alt={card.surnom}
                className="w-full h-48 object-cover"
              />
            )}
            <div className="p-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">{card.surnom}</h3>
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 text-yellow-500" />
                  <span className="text-sm font-medium">{calculateTotalScore(card)}</span>
                </div>
              </div>
              <div className="mt-4 flex justify-between items-center">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  card.rarete === 'GOLD' 
                    ? 'bg-yellow-100 text-yellow-800'
                    : card.rarete === 'RARE'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {card.rarete}
                </span>
                <button
                  onClick={() => handleDeleteCard(card.id)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}